import React from 'react';

import moment from 'moment';

import Avatar from '~/components/Avatar';
import { FixedDetail } from '~/components/Details/FixedDetails';
import { getTaskStatusImageAndLabel } from '~/components/Details/getStatus';
import { IStartEvent } from '~/store/modules/activities/types';
import { ITask } from '~/store/modules/tasks/types';

const isUserTask = (data?: unknown): data is ITask => {
  const task = data as ITask | undefined;
  return !!task && !!task.taskDefinitionKey;
};

const getResponsibleForTask = (
  data?: ITask | IStartEvent,
): string | undefined => {
  if (data) {
    if (isUserTask(data) && data.assigneeName) {
      return data.assigneeName;
    }
    if (data.registradoPor) {
      return data.registradoPor;
    }
  }
  return undefined;
};

const displayResponsibleForTask = (data?: ITask | IStartEvent): string => {
  return getResponsibleForTask(data) ?? 'Sem responsável';
};

const getExecutedStatus = (
  data?: ITask | IStartEvent,
): 'No prazo' | 'Atrasada' | null => {
  const status = getTaskStatusImageAndLabel(data?.state).name;
  if (status === 'Concluída') return 'No prazo';
  if (status === 'Concluída com atraso') return 'Atrasada';
  return null;
};

const listFixedDetails = (data?: ITask | IStartEvent): Array<FixedDetail> => {
  return [
    {
      label: 'Data de criação',
      value: data?.startTime
        ? !data?.startTime.includes('-0300')
          ? new Date(data?.startTime).toLocaleDateString('pt-br', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              timeZone: 'utc',
              hour: 'numeric',
              minute: 'numeric',
            })
          : new Date(data?.startTime).toLocaleDateString('pt-br', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: 'numeric',
              minute: 'numeric',
            })
        : 'Sem data',
    },
    {
      label:
        data?.state === 'EXTERNALLY_TERMINATED' ||
        data?.state === 'INTERNALLY_TERMINATED'
          ? 'Data de cancelamento'
          : 'Data de conclusão',
      value: data?.endTime
        ? !data?.endTime.includes('-0300')
          ? new Date(data?.endTime).toLocaleDateString('pt-br', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              timeZone: 'utc',
              hour: 'numeric',
              minute: 'numeric',
            })
          : new Date(data?.endTime).toLocaleDateString('pt-br', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: 'numeric',
              minute: 'numeric',
            })
        : 'Aguardando conclusão',
    },
    {
      label: isUserTask(data) ? 'Responsável da tarefa' : 'Registrado por',
      value: displayResponsibleForTask(data),
      icon: <Avatar name={getResponsibleForTask(data)} size={16} />,
    },
    {
      label: 'Executada',
      value: getExecutedStatus(data),
      renderCondition: !!getExecutedStatus(data),
    },
    {
      label: 'Cancelado por',
      value: (data as any)?.canceladoPor ?? 'Sem responsável',
      icon: <Avatar name={data?.registradoPor} size={16} />,
      renderCondition:
        data?.state === 'EXTERNALLY_TERMINATED' ||
        data?.state === 'INTERNALLY_TERMINATED',
    },
    {
      label: 'Motivo do cancelamento',
      value:
        (data as any)?.wedo_justificativaDoCancelamento ??
        'Processo cancelado por suas execuções',
      renderCondition:
        data?.state === 'EXTERNALLY_TERMINATED' ||
        data?.state === 'INTERNALLY_TERMINATED',
    },
    {
      label:
        (data as any)?.candidateGroups?.length > 1
          ? 'Funções elegíveis'
          : 'Função elegível',
      value:
        (data as any)?.candidateGroups
          ?.map((group: any) => group.name)
          .join(', ') ?? 'Sem função elegível',
      renderCondition: isUserTask(data),
    },
  ];
};

export default listFixedDetails;
